import { useState } from "react";
import {
  postVetcanChat,
  type VetcanChatResponse,
  type VetcanToolResult,
} from "../lib/vetcanApi";
import { getTenantBranding, getTenantConfig } from "../lib/tenantConfig";
import { resolveTenantKeyRuntime } from "../lib/runtimeTenant";

type ChatMessageKind = "text" | "action" | "error" | "transport_error";

type ChatMessage = {
  id: string;
  role: "user" | "assistant";
  content: string;
  kind?: ChatMessageKind;
  escalate?: boolean;
  action?: string;
  referenceId?: string;
};

type SchedulingWizardStep =
  | "idle"
  | "service_type"
  | "time_window"
  | "contact_method"
  | "contact_details"
  | "confirm";

type SchedulingWizardMode = "schedule" | "staff_handoff";

type SchedulingWizardState = {
  step: SchedulingWizardStep;
  mode?: SchedulingWizardMode;
  serviceType?: string;
  timeWindow?: string;
  contactMethod?: string;
  contactPhone?: string;
};

const howItWorks = [
  {
    title: "Start in chat",
    body: "Choose book now, talk to staff, or ask an operations question.",
  },
  {
    title: "Share logistics",
    body: "Share visit details and your best contact method so booking can be completed safely.",
  },
  {
    title: "Booking + payment-safe completion",
    body: "Clear bookings can be completed in chat when available. When payment is required, payment-safe next steps are surfaced, including secure-link follow-up or staff confirmation when needed, and exceptions are routed with clear status tracking.",
  },
];

const makeId = (prefix: string) => {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return `${prefix}_${crypto.randomUUID()}`;
  }
  return `${prefix}_${Date.now().toString(36)}_${Math.random()
    .toString(36)
    .slice(2, 8)}`;
};

const initialMessage: ChatMessage = {
  id: makeId("msg"),
  role: "assistant",
  kind: "text",
  content:
    "Hi! I can help with hours, location, parking, scheduling, renewals, and payment-safe follow-up after booking when needed. I can't provide medical advice, but I can connect you with staff.",
};

const intentMenuPrompt =
  "Choose an option: 1) Book appointment, 2) Reschedule visit, 3) Hours + location, 4) Pricing + what to bring, 5) Talk to staff.";

const initialIntentMenuMessage: ChatMessage = {
  id: makeId("msg"),
  role: "assistant",
  kind: "text",
  content: intentMenuPrompt,
};
const CHAT_TEASER_URL = "/chat";

const formatToolAction = (action?: string) => {
  if (!action) return "Action";
  return action
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const normalizeInput = (value: string) => value.trim().toLowerCase();

const isGreeting = (value: string) =>
  /^(hi|hello|hey|good (morning|afternoon|evening)|yo)\b/i.test(
    value.trim()
  );

const isThanks = (value: string) =>
  /\b(thanks|thank you|thx|appreciate it)\b/i.test(value.trim());

const isGoodbye = (value: string) =>
  /^(bye|goodbye|see you|talk later|have a good day|have a nice day)\b/i.test(
    value.trim()
  );

const asksForHuman = (value: string) =>
  /\b(staff|human|agent|person|representative|talk to someone|call me|callback)\b/i.test(
    value.trim()
  );

const asksForMedicalGuidance = (value: string) =>
  /\b(medical|doctor|provider|clinician|symptom|diagnos|dose|dosage|treat|treatment|side effect|interaction|contraindication|pain|anxiety|depress|insomnia|thc|cbd|mg)\b/i.test(
    value.trim()
  );

const asksToBook = (value: string) =>
  /^(1)\b/.test(value) ||
  /\b(book|schedule|appointment|new visit)\b/i.test(value);

const asksToReschedule = (value: string) =>
  /^(2)\b/.test(value) || /\b(reschedule|change|move)\b/i.test(value);

const asksForHoursAndLocation = (value: string) =>
  /^(3)\b/.test(value) ||
  /\b(hours?|open|close|location|address|where)\b/i.test(value);

const asksForPricingOrDocs = (value: string) =>
  /^(4)\b/.test(value) ||
  /\b(price|cost|fee|pricing|bring|paperwork|documents?)\b/i.test(value);

const asksToTalkToStaff = (value: string) =>
  /^(5)\b/.test(value) || asksForHuman(value);

const hasEscalationToolResult = (results?: VetcanToolResult[]) =>
  Array.isArray(results) &&
  results.some(
    (result) =>
      /escalat|handoff|staff/i.test(result.action) ||
      /escalat|handoff|staff/i.test(result.message)
  );

const isCancelIntent = (value: string) =>
  /\b(nevermind|never mind|cancel|stop|abort|exit|quit|forget it)\b/i.test(
    value.trim()
  );

const isRestartIntent = (value: string) =>
  /\b(restart|start over|reset)\b/i.test(value.trim());

const isGarbageFieldValue = (value?: string) => {
  const normalized = normalizeInput(value || "");
  if (!normalized) return true;
  return /^(n\/a|na|none|nothing|idk|i don't know|dont know|not sure|unsure|skip|whatever)$/i.test(
    normalized
  );
};

const isForbiddenCapturedValue = (value?: string) => {
  if (!value) return true;
  return (
    isCancelIntent(value) ||
    isGoodbye(value) ||
    isRestartIntent(value) ||
    asksForHuman(value) ||
    asksForMedicalGuidance(value) ||
    isGarbageFieldValue(value)
  );
};

const toTelHref = (phone: string) => {
  const digits = phone.replace(/\D/g, "");
  if (digits.length < 10) return "#";
  return `tel:+${digits}`;
};

const splitFactLines = (lines: string[]) =>
  lines.flatMap((line) =>
    line
      .split(/\s*;\s*/g)
      .map((part) => part.trim())
      .filter(Boolean)
  );

const getClinicInitials = (clinicName: string) => {
  const words = clinicName
    .split(/\s+/)
    .map((word) => word.trim())
    .filter(Boolean);
  const firstTwo = words.slice(0, 2).map((word) => word[0] || "");
  return firstTwo.join("").toUpperCase() || "CL";
};

const normalizePhoneDigits = (value: string): string | null => {
  const digitsOnly = value.replace(/\D/g, "");
  if (digitsOnly.length < 10) return null;
  return digitsOnly.slice(-10);
};

const maskPhoneDigits = (digits?: string) => {
  if (!digits || digits.length < 4) return "unknown";
  return `***-***-${digits.slice(-4)}`;
};

type MenuChoice = 1 | 2 | 3 | 4 | 5;

const extractMenuChoice = (value: string): MenuChoice | null => {
  const match = value.match(/\b([1-5])\b/);
  if (!match) return null;
  const digit = Number(match[1]);
  if (digit >= 1 && digit <= 5) {
    return digit as MenuChoice;
  }
  return null;
};

const normalizeContactMethod = (value: string): "text" | "phone" | null => {
  const normalized = normalizeInput(value);
  const wantsText = /\b(text|sms)\b/.test(normalized);
  const wantsPhone = /\b(phone|call|voice)\b/.test(normalized);

  if (wantsText && !wantsPhone) return "text";
  if (wantsPhone && !wantsText) return "phone";
  return null;
};

export default function VetcanLanding({
  hostnameOverride,
}: {
  hostnameOverride?: string;
}) {
  const runtimeHost =
    hostnameOverride ||
    (typeof window !== "undefined" ? window.location.hostname : "");
  const runtimeSearch = typeof window !== "undefined" ? window.location.search : "";
  const tenantKey = resolveTenantKeyRuntime({
    hostname: runtimeHost,
    search: runtimeSearch,
  });
  const tenant = getTenantConfig(tenantKey);
  const tenantBranding = getTenantBranding(tenantKey);

  const locationLines =
    tenant.locationLines.length > 0
      ? tenant.locationLines
      : ["Contact staff for details."];
  const hoursLines =
    tenant.hoursLines.length > 0
      ? splitFactLines(tenant.hoursLines)
      : ["Contact staff for details."];
  const locationText = locationLines.join(", ");
  const hoursText = hoursLines.join("; ");
  const supportTagline =
    tenantBranding.supportTagline || "Clinic scheduling and support";
  const noMedicalAdviceNotice =
    tenant.noMedicalAdviceNotice ||
    "This chat can help with operations and scheduling only and does not provide medical advice.";
  const chatSubtitle = `${noMedicalAdviceNotice} Medical questions are escalated to staff.`;
  const pricingFacts =
    tenant.pricingFacts && tenant.pricingFacts.length > 0
      ? tenant.pricingFacts
      : [
          "Pricing is handled as operational guidance only. Staff can confirm the exact public path for your visit.",
        ];
  const bookingFacts =
    tenant.bookingFacts && tenant.bookingFacts.length > 0
      ? tenant.bookingFacts
      : ["Bring any scheduling details requested by staff."];
  const pricingSummary = pricingFacts.join(" ");
  const bookingSummary = bookingFacts.join(" ");

  const [messages, setMessages] = useState<ChatMessage[]>([
    initialMessage,
    initialIntentMenuMessage,
  ]);
  const [input, setInput] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [conversationId, setConversationId] = useState(() =>
    makeId("conversation")
  );
  const [lastCorrelationId, setLastCorrelationId] = useState<string>("n/a");
  const [wizard, setWizard] = useState<SchedulingWizardState>({
    step: "idle",
  });
  const [logoVisible, setLogoVisible] = useState(Boolean(tenantBranding.logoPath));

  const canSend = input.trim().length > 0 && !isSending;

  const addAssistantMessages = (newMessages: ChatMessage[]) => {
    setMessages((prev) => [...prev, ...newMessages]);
  };

  const startSchedulingFlow = (intro?: string) => {
    setWizard({
      step: "service_type",
      mode: "schedule",
    });
    addAssistantMessages([
      {
        id: makeId("msg"),
        role: "assistant",
        kind: "text",
        content:
          intro ||
          "Great. Let's get this scheduled. What type of service do you need (new visit, renewal, follow-up, other)?",
      },
    ]);
  };

  const startStaffHandoffFlow = (medicalRequest: boolean) => {
    setWizard({
      step: "time_window",
      mode: "staff_handoff",
    });
    addAssistantMessages([
      {
        id: makeId("msg"),
        role: "assistant",
        kind: "text",
        escalate: true,
        content: medicalRequest
          ? "I can't provide medical advice. I can connect you with staff. Please share your preferred contact window, whether you prefer phone or text, and the best contact number."
          : "I can connect you with staff. Please share your preferred contact window, whether you prefer phone or text, and the best contact number.",
      },
    ]);
  };

  const getCurrentStepPrompt = () => {
    if (wizard.step === "service_type") {
      return "Please share the visit type (new visit, renewal, follow-up, other).";
    }
    if (wizard.step === "time_window") {
      return "Please share your preferred appointment window (for example: today after 3pm or weekday mornings).";
    }
    if (wizard.step === "contact_method") {
      return "Please reply 'text' or 'phone'.";
    }
    if (wizard.step === "contact_details") {
      return "What is the best contact number? (10 digits)";
    }
    if (wizard.step === "confirm") {
      return "Please reply 'confirm' to submit, or 'restart' to begin again.";
    }
    return intentMenuPrompt;
  };

  const scrollToChat = () => {
    if (typeof document === "undefined") return;
    document.getElementById("chat")?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  };

  const handleBookNow = () => {
    startSchedulingFlow(
      "Great - let's book a visit. What type of service do you need (new visit, renewal, follow-up, other)?"
    );
    scrollToChat();
  };

  const handleTalkToStaff = () => {
    startStaffHandoffFlow(false);
    scrollToChat();
  };

  const handleSend = async () => {
    if (!canSend) return;
    const trimmed = input.trim();
    const normalizedInput = normalizeInput(trimmed);
    const correlationId = makeId("corr");

    const userMessage: ChatMessage = {
      id: makeId("msg"),
      role: "user",
      kind: "text",
      content: trimmed,
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");

    // Global interrupts: these override wizard capture at any step.
    if (isCancelIntent(trimmed)) {
      setWizard({ step: "idle" });
      addAssistantMessages([
        {
          id: makeId("msg"),
          role: "assistant",
          kind: "text",
          content: "No problem - I've canceled that request.",
        },
        {
          id: makeId("msg"),
          role: "assistant",
          kind: "text",
          content: intentMenuPrompt,
        },
      ]);
      return;
    }

    if (isGoodbye(trimmed)) {
      setWizard({ step: "idle" });
      addAssistantMessages([
        {
          id: makeId("msg"),
          role: "assistant",
          kind: "text",
          content:
            "Thanks for checking in. We're here 24/7 for operational support whenever you need us.",
        },
      ]);
      return;
    }

    if (asksToTalkToStaff(trimmed)) {
      if (wizard.mode === "staff_handoff" && wizard.step !== "idle") {
        addAssistantMessages([
          {
            id: makeId("msg"),
            role: "assistant",
            kind: "text",
            escalate: true,
            content: `You're already in staff handoff mode. ${getCurrentStepPrompt()}`,
          },
        ]);
        return;
      }
      startStaffHandoffFlow(false);
      return;
    }

    if (asksForMedicalGuidance(trimmed)) {
      if (wizard.mode === "staff_handoff" && wizard.step !== "idle") {
        addAssistantMessages([
          {
            id: makeId("msg"),
            role: "assistant",
            kind: "text",
            escalate: true,
            content: `I can't provide medical advice. ${getCurrentStepPrompt()}`,
          },
        ]);
        return;
      }
      startStaffHandoffFlow(true);
      return;
    }

    if (isRestartIntent(trimmed) && wizard.step !== "idle") {
      if (wizard.mode === "staff_handoff") {
        setWizard({
          step: "time_window",
          mode: "staff_handoff",
        });
        addAssistantMessages([
          {
            id: makeId("msg"),
            role: "assistant",
            kind: "text",
            content:
              "Restarted. What contact window works best for the staff handoff?",
          },
        ]);
        return;
      }

      startSchedulingFlow(
        "Restarted. What type of service do you need (new visit, renewal, follow-up, other)?"
      );
      return;
    }

    const menuChoice = wizard.step === "idle" ? extractMenuChoice(trimmed) : null;
    if (menuChoice === 1) {
      startSchedulingFlow(
        "Great - let's book a visit. What type of service do you need (new visit, renewal, follow-up, other)?"
      );
      return;
    }
    if (menuChoice === 2) {
      startSchedulingFlow(
        "Understood - let's reschedule. What type of service do you need to move?"
      );
      return;
    }
    if (menuChoice === 3) {
      addAssistantMessages([
        {
          id: makeId("msg"),
          role: "assistant",
          kind: "text",
          content: `Hours: ${hoursText}. Location: ${locationText}. Parking: ${tenant.parkingText}.`,
        },
      ]);
      return;
    }
    if (menuChoice === 4) {
      addAssistantMessages([
        {
          id: makeId("msg"),
          role: "assistant",
          kind: "text",
          content: `${pricingSummary} ${bookingSummary}`,
        },
      ]);
      return;
    }
    if (menuChoice === 5) {
      startStaffHandoffFlow(false);
      return;
    }

    if (wizard.step !== "idle") {
      if (wizard.step === "service_type") {
        if (isForbiddenCapturedValue(trimmed)) {
          addAssistantMessages([
            {
              id: makeId("msg"),
              role: "assistant",
              kind: "text",
              content:
                "Please share the visit type (new visit, renewal, follow-up, other).",
            },
          ]);
          return;
        }

        setWizard((prev) => ({
          ...prev,
          step: "time_window",
          mode: prev.mode || "schedule",
          serviceType: trimmed,
        }));
        addAssistantMessages([
          {
            id: makeId("msg"),
            role: "assistant",
            kind: "text",
            content:
              "Thanks. What is your preferred day/time window? For example: weekday mornings, this Friday after 3pm, or next week.",
          },
        ]);
        return;
      }

      if (wizard.step === "time_window") {
        if (normalizeContactMethod(trimmed)) {
          addAssistantMessages([
            {
              id: makeId("msg"),
              role: "assistant",
              kind: "text",
              content:
                "Before contact method, please share your preferred appointment window.",
            },
          ]);
          return;
        }
        if (normalizePhoneDigits(trimmed)) {
          addAssistantMessages([
            {
              id: makeId("msg"),
              role: "assistant",
              kind: "text",
              content:
                "Thanks. First, what appointment window works best?",
            },
          ]);
          return;
        }

        if (isForbiddenCapturedValue(trimmed)) {
          addAssistantMessages([
            {
              id: makeId("msg"),
              role: "assistant",
              kind: "text",
              content:
                "Please share your preferred appointment window (for example: today after 3pm or weekday mornings).",
            },
          ]);
          return;
        }

        setWizard((prev) => ({
          ...prev,
          step: "contact_method",
          timeWindow: trimmed,
        }));
        addAssistantMessages([
          {
            id: makeId("msg"),
            role: "assistant",
            kind: "text",
            content: "Got it. Do you prefer text or phone?",
          },
        ]);
        return;
      }

      if (wizard.step === "contact_method") {
        const normalizedMethod = normalizeContactMethod(trimmed);
        if (!normalizedMethod) {
          addAssistantMessages([
            {
              id: makeId("msg"),
              role: "assistant",
              kind: "text",
              content: "Please reply 'text' or 'phone'.",
            },
          ]);
          return;
        }

        setWizard((prev) => ({
          ...prev,
          step: "contact_details",
          contactMethod: normalizedMethod,
        }));
        addAssistantMessages([
          {
            id: makeId("msg"),
            role: "assistant",
            kind: "text",
            content: "What is the best contact number? (10 digits)",
          },
        ]);
        return;
      }

      if (wizard.step === "contact_details") {
        const normalizedPhone = normalizePhoneDigits(trimmed);
        if (!normalizedPhone) {
          addAssistantMessages([
            {
              id: makeId("msg"),
              role: "assistant",
              kind: "text",
              content:
                "Please share a valid contact number with at least 10 digits.",
            },
          ]);
          return;
        }

        setWizard((prev) => ({
          ...prev,
          step: "confirm",
          contactPhone: normalizedPhone,
        }));
        addAssistantMessages([
          {
            id: makeId("msg"),
            role: "assistant",
            kind: "text",
            content:
              "Reply 'confirm' to submit this request, or 'restart' to start over.",
          },
        ]);
        return;
      }

      if (wizard.step === "confirm") {
        if (/^(confirm|yes|y|send|done)\b/.test(normalizedInput)) {
          const isStaffHandoff = wizard.mode === "staff_handoff";
          const hasInvalidFields =
            (isStaffHandoff ? false : isForbiddenCapturedValue(wizard.serviceType)) ||
            isForbiddenCapturedValue(wizard.timeWindow) ||
            (wizard.contactMethod !== "text" && wizard.contactMethod !== "phone") ||
            !wizard.contactPhone;

          if (hasInvalidFields) {
            addAssistantMessages([
              {
                id: makeId("msg"),
                role: "assistant",
                kind: "error",
                content:
                  "I couldn't confirm that request. Please reply 'restart' to begin again.",
              },
            ]);
            return;
          }

          const maskedPhone = maskPhoneDigits(wizard.contactPhone);
          const action = isStaffHandoff
            ? "staff_handoff_captured"
            : "schedule_request_captured";
          const toolAction = isStaffHandoff
            ? "handoff_to_staff"
            : "create_callback";
          const requestType =
            isStaffHandoff
              ? "general_question"
              : /\brenew/.test(String(wizard.serviceType || "").toLowerCase())
                ? "renewal"
                : "scheduling";
          const summary = isStaffHandoff
            ? `Staff handoff captured: ${wizard.timeWindow} | ${wizard.contactMethod} | phone ending ${wizard.contactPhone?.slice(-4)}.`
            : `Request captured: ${wizard.serviceType || "visit"} | ${wizard.timeWindow} | ${wizard.contactMethod} | ${maskedPhone}.`;

          setIsSending(true);
          try {
            const callbackPayload = await postVetcanChat(
              {
                conversationId,
                correlationId,
                message: isStaffHandoff
                  ? "staff handoff callback request"
                  : "scheduling callback request",
                toolRequests: [
                  {
                    action: toolAction,
                    payload: {
                      name: "Web chat customer",
                      phone: wizard.contactPhone,
                      preferredTime: wizard.timeWindow,
                      requestType,
                      staffFollowupRequired: true,
                    },
                  },
                ],
              },
              tenantKey
            );

            if (
              typeof callbackPayload?.conversationId === "string" &&
              callbackPayload.conversationId
            ) {
              setConversationId(callbackPayload.conversationId);
            }
            if (
              typeof callbackPayload?.correlationId === "string" &&
              callbackPayload.correlationId
            ) {
              setLastCorrelationId(callbackPayload.correlationId);
            } else {
              setLastCorrelationId(correlationId);
            }

            const toolResult = Array.isArray(callbackPayload?.toolResults)
              ? callbackPayload.toolResults.find(
                  (result) => result.action === toolAction
                )
              : undefined;

            addAssistantMessages([
              {
                id: makeId("msg"),
                role: "assistant",
                kind: toolResult?.ok === false ? "error" : "action",
                action,
                referenceId:
                  toolResult?.entityId ||
                  toolResult?.correlationId ||
                  callbackPayload?.correlationId ||
                  correlationId,
                content:
                  toolResult?.message ||
                  summary,
              },
              {
                id: makeId("msg"),
                role: "assistant",
                kind: "text",
                escalate: isStaffHandoff,
                content: isStaffHandoff
                  ? "Staff handoff is queued for follow-up."
                  : "Booking details captured. If manual review is needed, front desk follow-up is queued. Operational assistance only. No diagnosis or medical advice.",
              },
              {
                id: makeId("msg"),
                role: "assistant",
                kind: "text",
                content: intentMenuPrompt,
              },
            ]);
            setWizard({ step: "idle" });
          } catch {
            setLastCorrelationId(correlationId);
            addAssistantMessages([
              {
                id: makeId("msg"),
                role: "assistant",
                kind: "error",
                content:
                  "I couldn't submit that request right now. Please try again or call/text staff.",
              },
            ]);
          } finally {
            setIsSending(false);
          }
          return;
        }

        addAssistantMessages([
          {
            id: makeId("msg"),
            role: "assistant",
            kind: "text",
            content:
              "Please reply 'confirm' to submit, or 'restart' to begin again.",
          },
        ]);
        return;
      }
    }

    if (isGreeting(trimmed)) {
      addAssistantMessages([
        {
          id: makeId("msg"),
          role: "assistant",
          kind: "text",
          content: `Hi there. I can help with front desk operations and scheduling. ${intentMenuPrompt}`,
        },
      ]);
      return;
    }

    if (isThanks(trimmed)) {
      addAssistantMessages([
        {
          id: makeId("msg"),
          role: "assistant",
          kind: "text",
          content: `Happy to help. ${intentMenuPrompt}`,
        },
      ]);
      return;
    }

    if (asksToBook(trimmed) || asksToReschedule(trimmed)) {
      startSchedulingFlow();
      return;
    }

    if (asksForHoursAndLocation(trimmed)) {
      addAssistantMessages([
        {
          id: makeId("msg"),
          role: "assistant",
          kind: "text",
          content: `Hours: ${hoursText}. Location: ${locationText}. Parking: ${tenant.parkingText}.`,
        },
      ]);
      return;
    }

    if (asksForPricingOrDocs(trimmed)) {
      addAssistantMessages([
        {
          id: makeId("msg"),
          role: "assistant",
          kind: "text",
          content: `${pricingSummary} ${bookingSummary}`,
        },
      ]);
      return;
    }

    setIsSending(true);

    try {
      const payload: VetcanChatResponse = await postVetcanChat(
        {
          conversationId,
          message: trimmed,
          correlationId,
        },
        tenantKey
      );
      if (typeof payload?.conversationId === "string" && payload.conversationId) {
        setConversationId(payload.conversationId);
      }
      if (typeof payload?.correlationId === "string" && payload.correlationId) {
        setLastCorrelationId(payload.correlationId);
      } else {
        setLastCorrelationId(correlationId);
      }

      const showEscalation = hasEscalationToolResult(payload?.toolResults);
      const replyMessage: ChatMessage = {
        id: makeId("msg"),
        role: "assistant",
        kind: "text",
        content:
          payload?.reply ||
          "Thanks! We can continue booking here, and staff will step in if manual review is needed.",
        escalate: showEscalation,
      };

      const toolMessages: ChatMessage[] = Array.isArray(payload?.toolResults)
        ? payload.toolResults.map((result) => ({
            id: makeId("msg"),
            role: "assistant",
            kind: result.ok ? "action" : "error",
            content: result.message,
            action: result.action,
            referenceId: result.entityId || result.correlationId,
          }))
        : [];

      const backendMessages = [...toolMessages];
      if (showEscalation && payload?.escalate) {
        backendMessages.push({
          id: makeId("msg"),
          role: "assistant",
          kind: "text",
          escalate: true,
          content:
            "Escalation requested by workflow action. Staff will follow up.",
        });
      }

      setMessages((prev) => [...prev, replyMessage, ...backendMessages]);
    } catch (error) {
      setLastCorrelationId(correlationId);
      const detail =
        error instanceof Error && error.message
          ? error.message
          : "Chat backend unreachable. Check VITE_VETCAN_API_BASE_URL or Vite /api proxy.";
      setMessages((prev) => [
        ...prev,
        {
          id: makeId("msg"),
          role: "assistant",
          kind: "transport_error",
          content: detail,
          escalate: false,
        },
      ]);
    } finally {
      setIsSending(false);
    }
  };

  const faqItems = [
    {
      question: "What can I do here?",
      answer:
        "You can book or reschedule on first contact when possible, get operations help for hours, location, parking, and renewals, and request staff help when needed. When payment is required, payment-safe next steps are used, with secure-link or staff-confirmed follow-up when needed.",
    },
    {
      question: "What are the current public booking options?",
      answer: pricingSummary,
    },
    {
      question: "What should I have ready before booking?",
      answer: bookingSummary,
    },
    {
      question: "What are your hours?",
      answer: hoursText,
    },
    {
      question: "Where are you located?",
      answer: locationText,
    },
    {
      question: "How do I request a renewal by phone?",
      answer: `Renew by telephone (no video): ${tenant.renewalsPhone}.`,
    },
    {
      question: "How can I call or text staff?",
      answer: `Call or text 24/7: ${tenant.supportPhone}.`,
    },
    {
      question: "Do you provide medical advice in chat?",
      answer:
        "No. This chat provides operational support only and routes medical questions to staff.",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 via-white to-slate-100 text-slate-900">
      <main className="mx-auto w-full max-w-6xl px-6 pb-28 pt-8 md:pb-12">
        <section className="grid gap-8 lg:grid-cols-[1.02fr,0.98fr]">
          <div className="rounded-3xl border border-emerald-100 bg-white p-8 shadow-sm">
            <div className="flex items-center gap-3">
              {logoVisible && tenant.logoPath ? (
                <img
                  src={tenant.logoPath}
                  alt={`${tenant.clinicName} logo`}
                  className="h-12 w-auto"
                  onError={() => {
                    setLogoVisible(false);
                  }}
                />
              ) : (
                <div
                  aria-label={`${tenant.clinicName} logo fallback`}
                  className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-100 text-xs font-semibold text-emerald-900"
                >
                  {getClinicInitials(tenant.clinicName)}
                </div>
              )}
              <div>
                <p className="text-lg font-semibold text-emerald-950">
                  {tenant.clinicName}
                </p>
                <p className="text-sm text-slate-600">{supportTagline}</p>
              </div>
            </div>

            <h1 className="mt-6 text-4xl font-semibold leading-tight text-emerald-950 sm:text-5xl">
              Fast answers for hours, location, scheduling, and renewals.
            </h1>
            <p className="mt-4 text-base text-slate-700">
              Get started with chat to review scheduling options and complete next
              steps quickly, with staff routing when the path is unclear. This
              experience is focused on front-desk operations and conversions.
            </p>
            <p className="mt-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              {noMedicalAdviceNotice}
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleBookNow}
                className="rounded-full bg-emerald-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-800"
              >
                Book now
              </button>
              <button
                type="button"
                onClick={handleTalkToStaff}
                className="rounded-full border border-emerald-200 px-5 py-3 text-sm font-semibold text-emerald-900 transition hover:bg-emerald-50"
              >
                Talk to staff
              </button>
              <a
                href={toTelHref(tenant.supportPhone)}
                className="rounded-full border border-emerald-200 px-5 py-3 text-sm font-semibold text-emerald-900 transition hover:bg-emerald-50"
              >
                Call or text 24/7: {tenant.supportPhone}
              </a>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-2">
              <article className="rounded-2xl border border-emerald-100 bg-emerald-50/50 p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
                  Current public booking options
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {pricingFacts.map((fact) => (
                    <li key={fact}>• {fact}</li>
                  ))}
                </ul>
              </article>
              <article className="rounded-2xl border border-emerald-100 bg-white p-4">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
                  Before you book
                </p>
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {bookingFacts.map((fact) => (
                    <li key={fact}>• {fact}</li>
                  ))}
                </ul>
              </article>
            </div>
          </div>

          <section
            id="chat"
            className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-semibold">{tenant.clinicName} Chat</p>
                <p className="text-xs text-slate-500">Operations and scheduling</p>
              </div>
              <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs text-emerald-800">
                Live
              </span>
            </div>
            <p className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {chatSubtitle}
            </p>
            <p className="mt-2 break-all text-[11px] text-slate-500">
              Conversation: {conversationId} | Last correlation:{" "}
              {lastCorrelationId}
            </p>
            <div className="mt-4 h-80 space-y-4 overflow-y-auto rounded-2xl bg-slate-50 p-4">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${
                    message.role === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm shadow-sm ${
                      message.role === "user"
                        ? "bg-emerald-700 text-white"
                        : message.kind === "transport_error"
                          ? "border border-amber-200 bg-amber-50 text-amber-900"
                          : message.kind === "action"
                            ? "border border-emerald-200 bg-emerald-50 text-emerald-900"
                            : message.kind === "error"
                              ? "border border-rose-200 bg-rose-50 text-rose-900"
                              : "bg-white text-slate-800"
                    }`}
                  >
                    {message.kind === "action" || message.kind === "error" ? (
                      <div className="space-y-2">
                        <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                          {message.kind === "action"
                            ? "Action confirmed"
                            : "Action needs attention"}
                        </div>
                        <p className="text-sm font-semibold">
                          {formatToolAction(message.action)}
                        </p>
                        <p className="text-sm">{message.content}</p>
                        {message.referenceId && (
                          <p className="text-xs text-slate-500">
                            Reference ID: {message.referenceId}
                          </p>
                        )}
                      </div>
                    ) : (
                      <p className="whitespace-pre-line">{message.content}</p>
                    )}
                    {message.escalate && (
                      <p className="mt-2 text-xs text-emerald-700">
                        Escalation requested - staff will follow up.
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 flex items-center gap-3">
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                placeholder="Ask about scheduling, renewals, hours..."
                className="flex-1 rounded-full border border-slate-200 px-4 py-3 text-sm focus:border-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-700"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    void handleSend();
                  }
                }}
              />
              <button
                type="button"
                onClick={handleSend}
                disabled={!canSend}
                className="rounded-full bg-emerald-700 px-5 py-3 text-sm font-semibold text-white transition hover:bg-emerald-800 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {isSending ? "Sending" : "Send"}
              </button>
            </div>
          </section>
        </section>

        <section className="mt-12">
          <h2 className="text-3xl font-semibold text-emerald-950">How it works</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {howItWorks.map((step, index) => (
              <article
                key={step.title}
                className="rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm"
              >
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
                  Step {index + 1}
                </p>
                <h3 className="mt-2 text-lg font-semibold text-slate-900">
                  {step.title}
                </h3>
                <p className="mt-2 text-sm text-slate-700">{step.body}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mt-12 grid gap-4 md:grid-cols-3">
          <article className="rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
              Hours
            </p>
            <p className="mt-2 whitespace-pre-line text-sm text-slate-700">
              {hoursLines.join("\n")}
            </p>
          </article>
          <article className="rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
              Location
            </p>
            <p className="mt-2 whitespace-pre-line text-sm text-slate-700">
              {locationLines.join("\n")}
            </p>
          </article>
          <article className="rounded-2xl border border-emerald-100 bg-white p-5 shadow-sm">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-700">
              Parking
            </p>
            <p className="mt-2 text-sm text-slate-700">{tenant.parkingText}</p>
          </article>
        </section>

        <section className="mt-12 grid gap-8 lg:grid-cols-[1.08fr,0.92fr]">
          <div className="rounded-3xl border border-emerald-100 bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-semibold text-emerald-950">FAQ</h2>
            <div className="mt-5 space-y-3">
              {faqItems.map((item) => (
                <details
                  key={item.question}
                  className="rounded-2xl border border-emerald-100 bg-emerald-50/40 px-5 py-4"
                >
                  <summary className="cursor-pointer text-sm font-semibold text-slate-900">
                    {item.question}
                  </summary>
                  <p className="mt-2 text-sm text-slate-700">{item.answer}</p>
                </details>
              ))}
            </div>
          </div>

          <aside className="rounded-3xl border border-emerald-100 bg-white p-8 shadow-sm">
            <h2 className="text-2xl font-semibold text-emerald-950">Contact</h2>
            <p className="mt-3 text-sm text-slate-700">
              Renew by telephone (no video): {tenant.renewalsPhone}
            </p>
            <p className="mt-2 text-sm text-slate-700">
              Call or text 24/7: {tenant.supportPhone}
            </p>
            <div className="mt-6 grid gap-3">
              <a
                href={toTelHref(tenant.renewalsPhone)}
                className="rounded-full border border-emerald-200 px-4 py-2 text-center text-sm font-semibold text-emerald-900 transition hover:bg-emerald-50"
              >
                Renewals line
              </a>
              <a
                href={toTelHref(tenant.supportPhone)}
                className="rounded-full border border-emerald-200 px-4 py-2 text-center text-sm font-semibold text-emerald-900 transition hover:bg-emerald-50"
              >
                Call or text support
              </a>
            </div>
            <p className="mt-6 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs text-amber-900">
              {noMedicalAdviceNotice}
            </p>
          </aside>
        </section>
      </main>

      <footer className="bg-slate-950 text-slate-200">
        <div className="mx-auto flex w-full max-w-6xl flex-col gap-4 px-6 py-8 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="text-sm font-semibold">{tenant.clinicName}</p>
            <p className="text-xs text-slate-400">{supportTagline}</p>
          </div>
          <p className="text-xs text-slate-400">
            {noMedicalAdviceNotice}
          </p>
        </div>
        <div className="border-t border-white/10 py-4 text-center text-xs text-slate-500">
          No medical advice. Operations and scheduling support only.
        </div>
      </footer>

      <div className="fixed inset-x-0 bottom-0 z-40 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-6px_24px_rgba(15,23,42,0.12)] backdrop-blur md:hidden">
        <div className="mx-auto flex w-full max-w-6xl gap-2">
          <button
            type="button"
            onClick={handleBookNow}
            className="flex-1 rounded-full bg-emerald-700 px-4 py-3 text-sm font-semibold text-white transition hover:bg-emerald-800"
          >
            Book now
          </button>
          <button
            type="button"
            onClick={handleTalkToStaff}
            className="flex-1 rounded-full border border-emerald-200 px-4 py-3 text-sm font-semibold text-emerald-900 transition hover:bg-emerald-50"
          >
            Talk to staff
          </button>
        </div>
      </div>

      <a
        href={CHAT_TEASER_URL}
        target="_blank"
        rel="noreferrer"
        className="fixed bottom-20 right-4 z-50 inline-flex items-center rounded-full bg-emerald-700 px-4 py-2.5 text-xs font-semibold text-white shadow-[0_10px_30px_rgba(5,150,105,0.35)] transition hover:bg-emerald-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:ring-offset-2 md:bottom-6 md:right-6 md:text-sm"
      >
        Need help now? Chat with us
      </a>
    </div>
  );
}
